import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Avatar, Stack, Chip, Button, IconButton,
  CircularProgress, Tabs, Tab, Menu, MenuItem,
} from '@mui/material';
import { ArrowBack, PersonAdd, PersonRemove, ScubaDiving, People, Place, WorkspacePremium, Person, Badge, Tune, Notifications, Lock, ManageAccounts, MailOutline, MoreVert, VolumeOff, VolumeUp, Block, Forum, Info, Map as MapIcon } from '@mui/icons-material';
import { useUserProfile } from '../hooks/useUserProfile';
import { authService } from '../services/authService';
import PostCard from './PostCard';
import { postService, Post } from '../services/postService';
import BlockUserDialog from './BlockUserDialog';
import { muteService } from '../services/muteService';
import { useNotification } from './NotificationContext';
import { usePreferencesSync } from '../hooks/usePreferencesSync';
import ProfileEditTab from './ProfileEditTab';
import ProfileSettingsTabs, { ProfileSettingsTab } from './ProfileSettingsTabs';
import DiscoverClubsPanel from './DiscoverClubsPanel';
import ProfileDiveMap from './ProfileDiveMap';
import {
  getSpecialtyCertificationDisplayName,
  getGasCertificationDisplayName,
  getFreediveCertificationDisplayName,
  getFreediveSpecialtyDisplayName,
} from '../utils/optionsManager';

type OwnProfileTab = 'profile' | ProfileSettingsTab;
type ProfileTab = 'posts' | 'about' | 'map' | OwnProfileTab;

function metersToFeet(m: number) {
  return Math.round(m * 3.28084);
}

// The four flag maps beyond "highest certification" that GetProfile returns under
// certifications — each is a Record<key, boolean>, loosely typed since it arrives as
// raw JSON. Held keys become chips using the matching optionsManager display-name helper.
const CERTIFICATION_FLAG_GROUPS: Array<{
  key: string;
  displayName: (cert: string) => string;
}> = [
  { key: 'specialtyCertifications', displayName: (c) => getSpecialtyCertificationDisplayName(c as any) },
  { key: 'gasCertifications', displayName: (c) => getGasCertificationDisplayName(c as any) },
  { key: 'freediveCertifications', displayName: (c) => getFreediveCertificationDisplayName(c as any) },
  { key: 'freediveSpecialtyCertifications', displayName: (c) => getFreediveSpecialtyDisplayName(c as any) },
];

function heldCertificationLabels(certifications: Record<string, unknown> | null | undefined): string[] {
  if (!certifications) return [];
  const labels: string[] = [];
  for (const group of CERTIFICATION_FLAG_GROUPS) {
    const flags = certifications[group.key];
    if (!flags || typeof flags !== 'object') continue;
    for (const [cert, held] of Object.entries(flags as Record<string, unknown>)) {
      if (held === true) {
        try {
          labels.push(group.displayName(cert));
        } catch {
          // Unknown key (e.g. a future cert this build doesn't know about yet) — skip it
        }
      }
    }
  }
  return labels;
}

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [currentUser] = useState(authService.getUser());
  const { profile, loading, error, refresh, follow, unfollow, followLoading } = useUserProfile(userId ?? null);
  const [activeTab, setActiveTab] = useState<ProfileTab>('posts');
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [muteLoading, setMuteLoading] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);

  // /u/:userId is a top-level route, so landing here directly (deep link, refresh,
  // or nav straight to a profile) never mounts DiveExplorer. Without this
  // the saved account preferences are never pulled down, and every save in the
  // settings tabs below spreads a stale `customSettings` — silently reverting
  // other settings.
  usePreferencesSync();

  const isOwnProfile = currentUser?.userId === userId;

  // The author-posts endpoint already filters to what this viewer is allowed to see.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setPostsLoading(true);
    postService.getPostsByAuthor(userId)
      .then(p => { if (!cancelled) setPosts(p.filter(x => !x.repostedFromPostId)); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setPostsLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const handleFollowToggle = async () => {
    if (!currentUser) {
      showError('Sign in to follow other divers');
      return;
    }
    try {
      if (profile?.isFollowing) {
        await unfollow();
        showSuccess('Unfollowed');
      } else {
        await follow();
        showSuccess('Following!');
      }
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to update follow status');
    }
  };

  const handleMuteToggle = async () => {
    if (!currentUser || !profile) return;
    setMenuAnchor(null);
    setMuteLoading(true);
    try {
      if (profile.isMuted) {
        await muteService.unmute(profile.userId);
        showSuccess('Unmuted');
      } else {
        await muteService.mute(profile.userId);
        showSuccess(`You won't see ${profile.displayName}'s posts or notifications anymore`);
      }
      refresh();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to update mute status');
    } finally {
      setMuteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 4, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography color="text.secondary">This diver's profile couldn't be found.</Typography>
      </Box>
    );
  }

  const certificationLabels = heldCertificationLabels(profile.certifications);
  const hasMap = !!profile.publicDiveSites && profile.publicDiveSites.length > 0;
  const hasFreedivePBs = !!profile.freedivePersonalBests && (
    profile.freedivePersonalBests.staticApneaSeconds != null ||
    profile.freedivePersonalBests.dynamicApneaMeters != null ||
    profile.freedivePersonalBests.depthMeters != null
  );
  const featuredPosts = posts.filter(p => p.isFeatured);
  const otherPosts = posts.filter(p => !p.isFeatured);

  const handleFeatureChanged = (updated: Post) =>
    setPosts(prev => prev.map(p => (p.postId === updated.postId ? updated : p)));
  const handleDeletePost = async (postId: string) => {
    try {
      await postService.deletePost(postId);
      setPosts(prev => prev.filter(p => p.postId !== postId));
      showSuccess('Post deleted');
    } catch {
      showError('Failed to delete post');
    }
  };

  const renderPost = (post: Post) => (
    <PostCard
      key={post.postId}
      post={post}
      currentUserId={currentUser?.userId ?? null}
      onDelete={handleDeletePost}
      onFeatureChanged={handleFeatureChanged}
    />
  );

  return (
    <Box sx={{ px: { xs: 0, sm: 3 }, pb: 4, maxWidth: 760, mx: 'auto' }}>
      <IconButton onClick={() => navigate(-1)} sx={{ mt: 1, mb: 1, ml: { xs: 1, sm: 0 } }}>
        <ArrowBack />
      </IconButton>

      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        {/* Cover banner — a plain brand gradient until the diver adds a photo */}
        <Box
          sx={{
            height: { xs: 120, sm: 180 },
            backgroundColor: 'primary.dark',
            backgroundImage: profile.coverPhotoUrl
              ? `url(${profile.coverPhotoUrl})`
              : (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.light} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5 }}>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ mt: { xs: -5, sm: -6 } }}>
            <Avatar
              src={profile.profilePhotoUrl ?? undefined}
              sx={{
                width: { xs: 88, sm: 112 },
                height: { xs: 88, sm: 112 },
                bgcolor: 'primary.main',
                fontWeight: 700,
                fontSize: 36,
                border: 4,
                borderColor: 'background.paper',
              }}
            >
              {!profile.profilePhotoUrl && (initials(profile.displayName) || '?')}
            </Avatar>

            {!isOwnProfile && (
              <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
                <Button
                  variant={profile.isFollowing ? 'outlined' : 'contained'}
                  color={profile.isFollowing ? 'inherit' : 'primary'}
                  startIcon={followLoading ? <CircularProgress size={16} /> : profile.isFollowing ? <PersonRemove /> : <PersonAdd />}
                  disabled={followLoading}
                  onClick={handleFollowToggle}
                  sx={{ minHeight: 40 }}
                >
                  {profile.isFollowing ? 'Unfollow' : 'Follow'}
                </Button>
                {currentUser && (
                  <Button
                    variant="outlined"
                    startIcon={<MailOutline />}
                    onClick={() => navigate(`/messages?with=${profile.userId}`)}
                    sx={{ minHeight: 40 }}
                  >
                    Message
                  </Button>
                )}
                {currentUser && (
                  <>
                    <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ minHeight: 40, minWidth: 40 }}>
                      <MoreVert />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                      <MenuItem onClick={handleMuteToggle} disabled={muteLoading}>
                        {profile.isMuted ? <VolumeUp fontSize="small" sx={{ mr: 1 }} /> : <VolumeOff fontSize="small" sx={{ mr: 1 }} />}
                        {profile.isMuted ? 'Unmute' : 'Mute'}
                      </MenuItem>
                      {profile.userId !== currentUser.userId && (
                        <MenuItem onClick={() => { setMenuAnchor(null); setBlockOpen(true); }} sx={{ color: 'error.main' }}>
                          <Block fontSize="small" sx={{ mr: 1 }} /> Block
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </Stack>
            )}
          </Stack>

          <Typography variant="h4" sx={{ fontWeight: 800, mt: 1.5, overflowWrap: 'anywhere' }}>
            {profile.displayName}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5, color: 'text.secondary' }}>
            {profile.homeSiteName && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Place fontSize="small" />
                <Typography variant="body2">{profile.homeSiteName}</Typography>
              </Stack>
            )}
            {profile.certifications?.highestCertification != null && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <WorkspacePremium fontSize="small" />
                <Typography variant="body2">{String(profile.certifications.highestCertification)}</Typography>
              </Stack>
            )}
            {profile.divingSinceYear && <Typography variant="body2">Diving since {profile.divingSinceYear}</Typography>}
          </Stack>

          {profile.bio && (
            <Typography variant="body1" sx={{ mt: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {profile.bio}
            </Typography>
          )}

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{profile.followerCount}</strong> follower{profile.followerCount !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{profile.followingCount}</strong> following
            </Typography>
            {profile.isBuddy && (
              <Chip icon={<People sx={{ fontSize: 16 }} />} label="Dive Buddy" size="small" color="success" />
            )}
            {!isOwnProfile && (profile.mutualBuddyCount ?? 0) > 0 && (
              <Chip
                size="small"
                variant="outlined"
                icon={<People sx={{ fontSize: 16 }} />}
                label={`${profile.mutualBuddyCount} mutual buddy${profile.mutualBuddyCount === 1 ? '' : 'ies'}`}
              />
            )}
          </Stack>

          {profile.specialtyTags.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {profile.specialtyTags.map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Stack>
          )}
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_e, value: ProfileTab) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab value="posts" label="Posts" icon={<Forum />} iconPosition="start" />
            <Tab value="about" label="About" icon={<Info />} iconPosition="start" />
            {hasMap && <Tab value="map" label="Dive map" icon={<MapIcon />} iconPosition="start" />}
            {isOwnProfile && <Tab value="profile" label="Edit profile" icon={<Person />} iconPosition="start" />}
            {isOwnProfile && <Tab value="certifications" label="Certifications" icon={<Badge />} iconPosition="start" />}
            {isOwnProfile && <Tab value="preferences" label="Preferences" icon={<Tune />} iconPosition="start" />}
            {isOwnProfile && <Tab value="notifications" label="Notifications" icon={<Notifications />} iconPosition="start" />}
            {isOwnProfile && <Tab value="privacy" label="Privacy" icon={<Lock />} iconPosition="start" />}
            {isOwnProfile && <Tab value="account" label="Account" icon={<ManageAccounts />} iconPosition="start" />}
          </Tabs>
        </Box>
      </Paper>

      <Box sx={{ mt: 3, px: { xs: 2, sm: 0 } }}>
        {activeTab === 'posts' && (
          <Box>
            {postsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!postsLoading && posts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary">
                  {isOwnProfile ? "You haven't posted yet" : `${profile.displayName} hasn't shared anything you can see yet`}
                </Typography>
              </Box>
            )}
            {featuredPosts.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="overline" color="text.secondary">Featured</Typography>
                {featuredPosts.map(renderPost)}
              </Box>
            )}
            {featuredPosts.length > 0 && otherPosts.length > 0 && (
              <Typography variant="overline" color="text.secondary">Recent</Typography>
            )}
            {otherPosts.map(renderPost)}
          </Box>
        )}

        {activeTab === 'about' && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ScubaDiving color="primary" />
                  <Typography variant="body1">
                    <strong>{profile.diveCountLabel}</strong> logged
                    {profile.lastDiveDate && ` · last dive ${new Date(profile.lastDiveDate).toLocaleDateString()}`}
                  </Typography>
                </Stack>
                {profile.maxDepthMeters != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                    Max depth: {profile.maxDepthMeters.toFixed(1)}m ({metersToFeet(profile.maxDepthMeters)}ft)
                  </Typography>
                )}
              </Stack>
            </Paper>

            {(certificationLabels.length > 0 || hasFreedivePBs) && (
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                {certificationLabels.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Certifications</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {certificationLabels.map(label => (
                        <Chip key={label} size="small" variant="outlined" label={label} />
                      ))}
                    </Stack>
                  </>
                )}
                {hasFreedivePBs && profile.freedivePersonalBests && (
                  <Box sx={{ mt: certificationLabels.length > 0 ? 2 : 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Freediving personal bests</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {profile.freedivePersonalBests.staticApneaSeconds != null && (
                        <Chip size="small" label={`Static: ${profile.freedivePersonalBests.staticApneaSeconds}s`} />
                      )}
                      {profile.freedivePersonalBests.dynamicApneaMeters != null && (
                        <Chip size="small" label={`Dynamic: ${profile.freedivePersonalBests.dynamicApneaMeters}m`} />
                      )}
                      {profile.freedivePersonalBests.depthMeters != null && (
                        <Chip size="small" label={`Depth: ${profile.freedivePersonalBests.depthMeters}m`} />
                      )}
                    </Stack>
                  </Box>
                )}
              </Paper>
            )}

            {isOwnProfile && <DiscoverClubsPanel />}
          </Stack>
        )}

        {activeTab === 'map' && hasMap && <ProfileDiveMap sites={profile.publicDiveSites!} />}

        {isOwnProfile && activeTab === 'profile' && <ProfileEditTab profile={profile} onUpdated={refresh} />}
        {isOwnProfile && activeTab !== 'posts' && activeTab !== 'about' && activeTab !== 'map' && activeTab !== 'profile' && (
          <ProfileSettingsTabs activeTab={activeTab} />
        )}
      </Box>

      <BlockUserDialog
        open={blockOpen}
        userId={profile.userId}
        displayName={profile.displayName}
        onClose={() => setBlockOpen(false)}
        onBlocked={() => { setBlockOpen(false); navigate('/feed'); }}
      />
    </Box>
  );
};

export default UserProfile;
